import type { ScreenAnchor, ScreenSpec } from './heroScene';

/** The 3D camera's vertical field of view (heroScene). Also sets the DOM
 *  stills' perspective, so a still and its twin project identically. */
export const SCENE_FOV = 32;

/** Who shows the floating screens. 'off': the DOM stills (no WebGL, reduced
 *  motion, a lost context); they may breathe. 'hold': a WebGL scene is
 *  loading or fading in under them, so they hold perfectly still. 'on': the
 *  3D twins have taken over and the stills are hidden. */
export type HeroHandoff = 'off' | 'hold' | 'on';

// The floating screens' media and resting layout (immersive redesign,
// 2026-09-24): shared by the DOM stills (HeroScreens) and the WebGL scene
// (HeroBackdrop), which reads the stills' boxes back with readScreenAnchors.

export interface HeroScreenDef extends ScreenSpec {
  /** The tool this screen opens (owner 2026-10-19: "make them clickable ... to the connected tool"). */
  to: string;
  /** What it is, for the click's label. */
  label: string;
  /** Phones/tablets: centre offset from the band's middle and height, both
   *  in band heights; stacking; world depth; tilt (deg); dimming. */
  ox: number;
  hs: number;
  z: number;
  depth: number;
  ry: number;
  rz: number;
  dim: number;
  /** Desktop (>= 1024px): centre as % of the stage, height as a share of it.
   *  lxs: the centre on laptops narrower than 1440px, where it sits further
   *  out so it clears the compass and the headline. */
  lx: string;
  lxs?: string;
  ly: string;
  lh: number;
  ldepth: number;
  lry: number;
  lrz: number;
  ldim: number;
}

export const HERO_SCREENS: HeroScreenDef[] = [
  // THE FLOATING CARDS (owner 2026-10-19: "why only two on each side? and why those? the storyboard should be up
  // there... the animated version of the music video should be up there... the chopshop media editing suite, the
  // create avatar feature"; "one shouldnt be bigger than any other"). Computers: three on each side since he cut two
  // from the left (2026-10-20), one column each, all one size, mirrored. Phones keep a row of five (the others have no size there: readScreenAnchors skips them).
  // Every card is 9:16 so none is wider than another.
  {
    id: 'corrido', to: '/music', label: 'Corrido music video', poster: '/promo/poster_belico.jpg', video: '/promo/corrido_belico.mp4', aspect: 9 / 16,
    ox: 0, hs: 1, z: 3, depth: 0.6, ry: 0, rz: 0, dim: 0,
    lx: '10%', lxs: '9%', ly: '22%', lh: 0.215, ldepth: 0, lry: 15, lrz: -3, ldim: 0.1,
  },
  // The left column's 2nd and 3rd cards are cut on computers (owner 2026-10-20: "cut these two from up there"):
  // the slot that held his avatar is gone, and the story video keeps only its phone place (lh 0 = no size there).
  {
    // It loops the captioned maple tree only (round 2): from 3.83s the render is plain overcast sky.
    id: 'story', to: '/tiktok', label: 'TikTok story video', poster: '/promo/poster_story_1.jpg', video: '/promo/story_clip_1.mp4', aspect: 9 / 16, exposure: 0.8, loopEnd: 3.7,
    ox: -0.46, hs: 0.82, z: 2, depth: -1, ry: 16, rz: -3.5, dim: 0.18,
    lx: '10%', lxs: '9%', ly: '68%', lh: 0, ldepth: 0, lry: 15, lrz: -3, ldim: 0.1,
  },
  {
    id: 'storybook', to: '/storybook', label: 'Illustrated storybook', poster: '/storybook/promo/style-animated3d-640.webp', aspect: 9 / 16,
    ox: 0, hs: 0, z: 1, depth: -2, ry: 0, rz: 0, dim: 0.3,
    lx: '10%', lxs: '9%', ly: '56.5%', lh: 0.215, ldepth: 0, lry: 15, lrz: -3, ldim: 0.1,
  },
  {
    // The editor moved left (2026-10-20) so each side keeps three.
    id: 'editor', to: '/editor', label: 'ChopShop media editor', poster: '/promo/chopbox-editor-800.webp', aspect: 9 / 16,
    ox: 0, hs: 0, z: 1, depth: -2, ry: 0, rz: 0, dim: 0.3,
    lx: '10%', lxs: '9%', ly: '91%', lh: 0.215, ldepth: 0, lry: 15, lrz: -3, ldim: 0.1,
  },
  {
    // The Living Cover: the owner's bear cover, moving.
    id: 'canvas', to: '/canvas', label: 'Living Cover for Spotify Canvas', poster: '/quicktools/canvas-poster-v2.jpg', video: '/quicktools/canvas-sample-v2.mp4', aspect: 9 / 16,
    ox: 0.84, hs: 0.7, z: 1, depth: -2.2, ry: -24, rz: 6, dim: 0.22,
    lx: '90%', lxs: '91%', ly: '22%', lh: 0.215, ldepth: 0, lry: -15, lrz: 3, ldim: 0.1,
  },
  {
    id: 'ad', to: '/ads', label: 'Ads and commercials', poster: '/promo/poster_ad_energy.jpg', video: '/promo/ad_energy_spot.mp4', aspect: 9 / 16,
    ox: 0.46, hs: 0.82, z: 2, depth: -1, ry: -16, rz: 3.5, dim: 0.18,
    lx: '90%', lxs: '91%', ly: '56.5%', lh: 0.215, ldepth: 0, lry: -15, lrz: 3, ldim: 0.1,
  },
  {
    // FaceShot-FaceLock (owner 2026-10-19: "use this image reference ... to create our image card"): the face picture he supplied, not his avatar.
    id: 'facelock', to: '/chopshop#casting-crew', label: 'FaceShot-FaceLock: scan your avatar', poster: '/promo/hero-facelock.webp', aspect: 9 / 16,
    ox: -0.84, hs: 0.64, z: 1, depth: -2.4, ry: 26, rz: -7, dim: 0.2,
    lx: '90%', lxs: '91%', ly: '91%', lh: 0.215, ldepth: 0, lry: -15, lrz: 3, ldim: 0.1,
  },
];

/** Read the stills' resting boxes back for the 3D scene, relative to `host`
 *  (the canvas box). The figure itself is never transformed, so its box is
 *  the untilted rest pose; tilt and depth come from the resolved CSS vars. */
export function readScreenAnchors(root: HTMLElement | null, host: HTMLElement | null): ScreenAnchor[] {
  if (!root || !host) return [];
  const hostBox = host.getBoundingClientRect();
  const out: ScreenAnchor[] = [];
  root.querySelectorAll<HTMLElement>('[data-hero-screen]').forEach((fig) => {
    const r = fig.getBoundingClientRect();
    if (r.width < 2 || r.height < 2) return;
    const cs = getComputedStyle(fig);
    const num = (name: string) => {
      const v = parseFloat(cs.getPropertyValue(name));
      return Number.isFinite(v) ? v : 0;
    };
    const card = fig.querySelector<HTMLElement>('.hero-screen__card');
    const radius = card ? parseFloat(getComputedStyle(card).borderTopLeftRadius) || 14 : 14;
    out.push({
      id: fig.dataset.heroScreen || '',
      x: r.left - hostBox.left,
      y: r.top - hostBox.top,
      w: r.width,
      h: r.height,
      depth: num('--depth-now'),
      ry: num('--ry-now'),
      rz: num('--rz-now'),
      dim: num('--dim-now'),
      radius,
    });
  });
  return out;
}

/** Give the DOM stills the 3D camera's projection (2026-09-25). The scene
 *  sees every screen from ONE eye above the canvas centre, SCENE_FOV tall;
 *  a plain `perspective(1500px)` on each card looked at it head-on instead,
 *  so a tilted outer screen came out ~10% narrower than its twin and the
 *  handoff showed doubled edges, then a size pop. Here each card's vanishing
 *  point moves to the canvas centre (--hero-dx/--hero-dy: card centre minus
 *  canvas centre) at the camera's eye distance (--hero-eye), and hero.css
 *  rotates about the card's own centre inside that. Unset (first paint, jsdom),
 *  the CSS falls back to the old per-card 1500px. */
export function syncStillProjection(root: HTMLElement | null, host: HTMLElement | null): void {
  if (!root || !host) return;
  const h = host.getBoundingClientRect();
  if (h.width < 2 || h.height < 2) return;
  const cx = h.left + h.width / 2;
  const cy = h.top + h.height / 2;
  const eye = h.height / (2 * Math.tan(((SCENE_FOV / 2) * Math.PI) / 180));
  root.style.setProperty('--hero-eye', `${eye.toFixed(1)}px`);
  root.querySelectorAll<HTMLElement>('[data-hero-screen]').forEach((fig) => {
    const card = fig.querySelector<HTMLElement>('.hero-screen__card');
    const r = fig.getBoundingClientRect();
    if (!card || r.width < 2 || r.height < 2) return;
    card.style.setProperty('--hero-dx', `${(r.left + r.width / 2 - cx).toFixed(2)}px`);
    card.style.setProperty('--hero-dy', `${(r.top + r.height / 2 - cy).toFixed(2)}px`);
  });
}
